import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import LoginPage from './LoginPage';
import JudgeApp from './judge/JudgeApp';
import LoggedOut from './LoggedOut';
import RubricPage from './judge/components/RubricPage';
import ProtectedRoute from './ProtectedRoute';
import AdminDashboard from './admin/AdminDashboard';
import TabulatorDashboard from './tabulator/TabulatorDashboard';
import RegistrationLanding from './registration/RegistrationLanding';
import SchoolRegistration from './registration/SchoolRegistration';
import DancerRegistration from './registration/DancerRegistration';
import StaffRegistration from './registration/StaffRegistration';
import ParentConsent from './registration/ParentConsent';
import ChoreographerSubmission from './registration/ChoreographerSubmission';
import WorkshopLeaderForm from './registration/WorkshopLeaderForm';
import TeacherLoginPage from './teacher/TeacherLoginPage';
import TeacherApp from './teacher/TeacherApp';
import TeacherProtectedRoute from './teacher/TeacherProtectedRoute';

// The bare "/" (ossdancefest.ca/app) used to show a 3-portal picker
// (Judges/Admin/Tabulators) — that picker is no longer reachable UI on a
// plain visit, since it was too easy to land on by accident; Judge/Admin/
// Tabulator each have their own direct-login URL instead (see
// directLoginPortal on their routes below), so nothing actually depends on
// the picker to sign in. The one case that still needs it: the OAuth
// callback sends someone here with ?error=unauthorized/server when they're
// logged in but lack the privilege for whatever portal they tried — that
// message still needs to be seen, so only a plain visit (no ?error) bounces
// externally; an error visit still shows the old picker page + message.
function HomeOrExternalRedirect() {
  const hasError = new URLSearchParams(window.location.search).has('error');
  if (hasError) return <LoginPage />;
  return <ExternalRedirect to="https://ossdancefest.ca" />;
}

function ExternalRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function App({ mode = "light", setMode }) {
  return (
    <Router basename="/app">
      <Routes>
        <Route path="/" element={<HomeOrExternalRedirect />} />
        <Route path="/rubric" element={<RubricPage />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        <Route
          path="/judge/*"
          element={
            <ProtectedRoute portal="judge" directLoginPortal="judge">
              <JudgeApp mode={mode} setMode={setMode} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute portal={['accountAdmin', 'eventAdmin', 'eventViewer', 'workshopAdmin', 'workshopViewer']} directLoginPortal="admin">
              <AdminDashboard mode={mode} setMode={setMode} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tabulator/*"
          element={
            <ProtectedRoute portal="tabulator" directLoginPortal="tabulator">
              <TabulatorDashboard />
            </ProtectedRoute>
          }
        />

        {/* Registration — public routes (no login required) */}
        <Route path="/registration" element={<RegistrationLanding />} />
        <Route path="/registration/school" element={<SchoolRegistration />} />
        <Route path="/registration/dancer" element={<DancerRegistration />} />
        <Route path="/registration/staff" element={<StaffRegistration />} />
        <Route path="/registration/parent-consent" element={<ParentConsent />} />
        <Route path="/registration/choreographer-entry" element={<ChoreographerSubmission />} />
        <Route path="/registration/workshop-leader" element={<WorkshopLeaderForm />} />

        {/* Teacher portal — separate entry point, isolated from other portals */}
        <Route path="/teacher/login" element={<TeacherLoginPage />} />
        <Route
          path="/teacher/*"
          element={
            <TeacherProtectedRoute>
              <TeacherApp />
            </TeacherProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
